export const REVIEW_SEVERITY_ORDER = [
    'critical',
    'high',
    'medium',
    'low',
    'unknown',
];

export const REVIEW_ACTION_URGENCY_ORDER = [
    'validate',
    'clarify',
    'negotiate',
    'document_in_writing',
    'accept_with_reserve',
    'monitor',
    'non_blocking_optimization',
    'unclassified',
];

export const REVIEW_ACTION_KINDS = {
    NEGOTIATE: 'negotiate',
    CLARIFY: 'clarify',
    VALIDATE: 'validate',
    DOCUMENT_IN_WRITING: 'document_in_writing',
    ACCEPT_WITH_RESERVE: 'accept_with_reserve',
    MONITOR: 'monitor',
    NON_BLOCKING_OPTIMIZATION: 'non_blocking_optimization',
    UNCLASSIFIED: 'unclassified',
};

const ACTION_PREFIXES = [
    {prefix: 'À négocier', kind: REVIEW_ACTION_KINDS.NEGOTIATE},
    {prefix: 'À clarifier avant signature', kind: REVIEW_ACTION_KINDS.CLARIFY},
    {
        prefix: 'À documenter par écrit',
        kind: REVIEW_ACTION_KINDS.DOCUMENT_IN_WRITING,
        displayKind: REVIEW_ACTION_KINDS.CLARIFY,
    },
    {prefix: 'À valider en priorité', kind: REVIEW_ACTION_KINDS.VALIDATE},
    {prefix: 'À accepter avec réserve', kind: REVIEW_ACTION_KINDS.ACCEPT_WITH_RESERVE},
    {prefix: 'À surveiller', kind: REVIEW_ACTION_KINDS.MONITOR},
    {
        prefix: 'Optimisation non bloquante',
        kind: REVIEW_ACTION_KINDS.NON_BLOCKING_OPTIMIZATION,
    },
    // English forms already present in persisted fixtures and review pipeline tests.
    {prefix: 'Negotiate', kind: REVIEW_ACTION_KINDS.NEGOTIATE},
    {prefix: 'Clarify before signature', kind: REVIEW_ACTION_KINDS.CLARIFY},
];

const DISPLAY_ACTION_KINDS = [
    REVIEW_ACTION_KINDS.NEGOTIATE,
    REVIEW_ACTION_KINDS.CLARIFY,
    REVIEW_ACTION_KINDS.VALIDATE,
    REVIEW_ACTION_KINDS.ACCEPT_WITH_RESERVE,
    REVIEW_ACTION_KINDS.MONITOR,
];

const REVIEW_COMMENT_TYPES = new Set(['risk', 'comparison_risk']);

export const normalizeReviewSortText = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export const getReviewSeverity = (risk) => {
    const rawRisk = risk && typeof risk === 'object'
        ? risk.level || risk.risk || risk.severity
        : risk;
    const normalized = normalizeReviewSortText(rawRisk);

    if (['critical', 'critique'].includes(normalized)) return 'critical';
    if (['high', 'eleve'].includes(normalized)) return 'high';
    if (['medium', 'moyen'].includes(normalized)) return 'medium';
    if (['low', 'faible'].includes(normalized)) return 'low';
    return 'unknown';
};

const compareByOrderedValues = (left, right, orderedValues) => {
    for (const value of orderedValues) {
        const leftMatches = left === value;
        const rightMatches = right === value;
        if (leftMatches !== rightMatches) return leftMatches ? -1 : 1;
    }
    return 0;
};

export const compareReviewSeverity = (left, right) =>
    compareByOrderedValues(getReviewSeverity(left), getReviewSeverity(right), REVIEW_SEVERITY_ORDER);

const hasExactPrefix = (action, prefix) => {
    if (!action.startsWith(prefix)) return false;
    const nextCharacter = action.charAt(prefix.length);
    return !nextCharacter || /[\s:–—-]/.test(nextCharacter);
};

export const parseReviewAction = (action) => {
    const rawAction = typeof action === 'string' ? action.trimStart() : '';
    const match = ACTION_PREFIXES.find(({prefix}) => hasExactPrefix(rawAction, prefix));

    if (!match) {
        return {
            kind: REVIEW_ACTION_KINDS.UNCLASSIFIED,
            displayKind: REVIEW_ACTION_KINDS.UNCLASSIFIED,
            prefix: null,
        };
    }

    return {
        kind: match.kind,
        displayKind: match.displayKind || match.kind,
        prefix: match.prefix,
    };
};

const getCommentType = (comment) => {
    const explicitType = comment?.comment_type || comment?.commentType;
    if (explicitType) return normalizeReviewSortText(explicitType);

    const genericType = normalizeReviewSortText(comment?.type || comment?.event_type);
    if (['risk', 'comparison_risk', 'comparison_change', 'error'].includes(genericType)) {
        return genericType;
    }
    return 'risk';
};

const isTechnicalPlaceholder = (comment) => {
    const rawMetadata = comment?.metadata_json || comment?.metadataJson || comment?.metadata || {};
    let metadata = rawMetadata;
    if (typeof rawMetadata === 'string') {
        try {
            metadata = JSON.parse(rawMetadata);
        } catch {
            metadata = {};
        }
    }
    return Boolean(
        comment?.error ||
        normalizeReviewSortText(comment?.event) === 'error' ||
        getCommentType(comment) === 'error' ||
        metadata?.technical_placeholder ||
        metadata?.locked_placeholder ||
        metadata?.commercial_locked
    );
};

export const isReviewDecisionPoint = (comment) =>
    comment?.id !== null &&
    comment?.id !== undefined &&
    String(comment.id).trim() !== '' &&
    REVIEW_COMMENT_TYPES.has(getCommentType(comment)) &&
    !isTechnicalPlaceholder(comment);

const asFiniteNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const getPage = (comment) => asFiniteNumber(
    comment?.page_number ??
    comment?.page ??
    comment?.pageNumber ??
    comment?.highlights?.[0]?.page_number ??
    comment?.highlights?.[0]?.page
);

const getBboxTop = (comment) => asFiniteNumber(
    comment?.bbox_top ??
    comment?.bbox?.top ??
    comment?.highlights?.[0]?.bbox?.top
);

const compareNullableNumbers = (left, right) => {
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return left === right ? 0 : (left < right ? -1 : 1);
};

const compareNormalizedText = (left, right) =>
    normalizeReviewSortText(left) === normalizeReviewSortText(right)
        ? 0
        : (normalizeReviewSortText(left) < normalizeReviewSortText(right) ? -1 : 1);

const compareRawText = (left, right) => {
    const leftText = String(left ?? '');
    const rightText = String(right ?? '');
    if (leftText === rightText) return 0;
    return leftText < rightText ? -1 : 1;
};

const getActionText = (comment) =>
    comment?.quick_view?.action || comment?.quickView?.action || comment?.suggested_action || '';

const toDecisionPoint = (comment) => {
    const parsedAction = parseReviewAction(getActionText(comment));
    return {
        id: comment.id,
        commentType: getCommentType(comment),
        severity: getReviewSeverity(comment.risk ?? comment.severity ?? comment.risk_level),
        title: comment.title || comment.quick_view?.risk || comment.quickView?.risk || '',
        action: getActionText(comment),
        actionKind: parsedAction.kind,
        actionDisplayKind: parsedAction.displayKind,
        description: comment.quick_view?.description || comment.quickView?.description || '',
        page: getPage(comment),
        bboxTop: getBboxTop(comment),
        ref: comment.ref || comment.reference || '',
        criterionId: comment.criterion_id || comment.criterionId || '',
        source: comment,
    };
};

export const compareReviewDecisionPoints = (left, right) => {
    const severityComparison = compareByOrderedValues(
        left.severity,
        right.severity,
        REVIEW_SEVERITY_ORDER
    );
    if (severityComparison) return severityComparison;

    const actionComparison = compareByOrderedValues(
        left.actionKind,
        right.actionKind,
        REVIEW_ACTION_URGENCY_ORDER
    );
    if (actionComparison) return actionComparison;

    return (
        compareNullableNumbers(left.page, right.page) ||
        compareNullableNumbers(left.bboxTop, right.bboxTop) ||
        compareNormalizedText(left.ref, right.ref) ||
        compareRawText(left.criterionId, right.criterionId) ||
        compareRawText(left.id, right.id)
    );
};

export const isReviewDecisionReady = ({isStreaming = false, criteria = []} = {}) =>
    !isStreaming &&
    (Array.isArray(criteria) ? criteria : []).some(
        (criterion) => normalizeReviewSortText(criterion?.status) === 'completed'
    );

export const buildReviewDecisionProjection = (comments = []) => {
    const points = (Array.isArray(comments) ? comments : [])
        .filter(isReviewDecisionPoint)
        .map(toDecisionPoint);

    const actionDistribution = Object.values(REVIEW_ACTION_KINDS).reduce(
        (distribution, kind) => ({...distribution, [kind]: 0}),
        {}
    );

    points.forEach((point) => {
        actionDistribution[point.actionDisplayKind] += 1;
    });

    return {
        pointsIdentified: points.length,
        attentionActionCount: points.filter(
            ({actionKind}) => actionKind !== REVIEW_ACTION_KINDS.NON_BLOCKING_OPTIMIZATION
        ).length,
        actionDistribution,
        visibleActionKinds: DISPLAY_ACTION_KINDS.filter(
            (kind) => actionDistribution[kind] > 0
        ),
        unclassifiedCount: actionDistribution[REVIEW_ACTION_KINDS.UNCLASSIFIED],
        topPriorities: [...points].sort(compareReviewDecisionPoints).slice(0, 3),
    };
};
